'use strict';

// Sealed deposit of slice S to the Shamir recovery service.
//
// The browser encrypts S to the service's public key, so web3keys.com relays a ciphertext it
// cannot open. Without this, the web3keys server would see C and S in the same request, which
// is enough to rebuild the mnemonic.
//
// Format v1: ECDH P-256 with a fresh ephemeral key per deposit → HKDF-SHA256 → AES-256-GCM.
//   salt = epk || recipient public key (raw, uncompressed), info = "shamir-deposit-v1"
//   AAD  = "shamir-deposit-v1|" + normalised email, so a sealed share only opens for that email.
// The service implements the same open() in its src/sealed.js; keep the two in step.

const { subtle, randomBytes } = require('./lib');
const { DEPOSIT_KEY } = require('./depositKey');

const V = 1;
const ALG = 'ECDH-P256+HKDF-SHA256+A256GCM';
const INFO = 'shamir-deposit-v1';
const enc = new TextEncoder();

const b64u = (u8) => {
  let s = '';
  for (const b of u8) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};
const unb64u = (s) => {
  const t = String(s).replace(/-/g, '+').replace(/_/g, '/');
  const bin = atob(t + '='.repeat((4 - (t.length % 4)) % 4));
  return Uint8Array.from(bin, (c) => c.charCodeAt(0));
};
const concat = (a, b) => { const o = new Uint8Array(a.length + b.length); o.set(a); o.set(b, a.length); return o; };
const normEmail = (e) => String(e || '').trim().toLowerCase();
const aad = (email) => enc.encode(INFO + '|' + normEmail(email));

async function aesKey(shared, epkRaw, recipientRaw) {
  const ikm = await subtle().importKey('raw', shared, 'HKDF', false, ['deriveKey']);
  return subtle().deriveKey(
    { name: 'HKDF', hash: 'SHA-256', salt: concat(epkRaw, recipientRaw), info: enc.encode(INFO) },
    ikm, { name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
}

/** Seal a Shamir share object for `email` to the service key { kid, publicKey (b64url raw P-256) }. */
async function sealShare(share, email, key = DEPOSIT_KEY) {
  if (!key || !key.publicKey || !key.kid) throw new Error('no deposit key configured');
  const recipientRaw = unb64u(key.publicKey);
  const recipient = await subtle().importKey('raw', recipientRaw, { name: 'ECDH', namedCurve: 'P-256' }, false, []);
  const eph = await subtle().generateKey({ name: 'ECDH', namedCurve: 'P-256' }, false, ['deriveBits']);
  const epkRaw = new Uint8Array(await subtle().exportKey('raw', eph.publicKey));
  const shared = await subtle().deriveBits({ name: 'ECDH', public: recipient }, eph.privateKey, 256);
  const k = await aesKey(shared, epkRaw, recipientRaw);
  const iv = randomBytes(12);
  const ct = new Uint8Array(await subtle().encrypt({ name: 'AES-GCM', iv, additionalData: aad(email) }, k, enc.encode(JSON.stringify(share))));
  return { v: V, alg: ALG, kid: key.kid, epk: b64u(epkRaw), iv: b64u(iv), ct: b64u(ct) };
}

/** Open a sealed share (used by tests and the offline mock; production opens it in the service). */
async function openShare(sealed, email, privateKey, recipientRaw) {
  if (!sealed || sealed.v !== V || sealed.alg !== ALG) throw new Error('unsupported sealed share');
  const epkRaw = unb64u(sealed.epk);
  const epk = await subtle().importKey('raw', epkRaw, { name: 'ECDH', namedCurve: 'P-256' }, false, []);
  const shared = await subtle().deriveBits({ name: 'ECDH', public: epk }, privateKey, 256);
  const k = await aesKey(shared, epkRaw, recipientRaw);
  const pt = await subtle().decrypt({ name: 'AES-GCM', iv: unb64u(sealed.iv), additionalData: aad(email) }, k, unb64u(sealed.ct));
  return JSON.parse(new TextDecoder().decode(pt));
}

module.exports = { sealShare, openShare, b64u, unb64u, ALG, V };
